import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const EraNode = ({ id, data }) => {
  const { eras = [], onUpdate } = data;
  const [expanded, setExpanded] = useState(true);
  
  // Initialize with at least one era if eras array is empty but we have legacy data
  const erasList = eras.length > 0 ? eras : 
    (data.reward !== undefined) ? 
      [{ reward: data.reward || 0, decay: data.decay || 0, halving: data.halving || 0, eraend: data.eraend || 0 }] : 
      [{ reward: 2400000000, decay: 0, halving: 1051920, eraend: 0 }];

  const handleAddEra = () => {
    const newEras = [...erasList, { reward: 0, decay: 0, halving: 0, eraend: 0 }];
    onUpdate?.({ eras: newEras });
  };

  const handleRemoveEra = (index) => {
    if (erasList.length <= 1) return;
    const newEras = erasList.filter((_, i) => i !== index);
    onUpdate?.({ eras: newEras });
  };

  const handleUpdateEra = (index, field, value) => {
    const newEras = [...erasList];
    newEras[index] = { ...newEras[index], [field]: value };
    onUpdate?.({ eras: newEras });
  };

  // Convert satoshis to display value
  const toCoins = (satoshis) => (satoshis || 0) / 100000000;
  const toSatoshis = (coins) => Math.floor((parseFloat(coins) || 0) * 100000000);

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="bg-slate-800 rounded-xl border-2 border-violet-500/50 shadow-xl min-w-[320px] max-w-[380px]"
    >
      {/* Header */}
      <div 
        className="bg-gradient-to-r from-violet-500 to-purple-600 rounded-t-lg px-3 py-2 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-white/20 flex items-center justify-center">
              <Clock className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white text-xs">Era Configuration</h3>
              <p className="text-white/70 text-[10px]">{erasList.length} era{erasList.length !== 1 ? 's' : ''} defined</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); handleAddEra(); }}
              className="w-6 h-6 rounded bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            >
              <Plus className="w-3 h-3 text-white" />
            </button>
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-white/70" />
            ) : (
              <ChevronDown className="w-4 h-4 text-white/70" />
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-2 space-y-2 max-h-[350px] overflow-y-auto custom-scrollbar">
              {erasList.map((era, index) => (
                <div key={index} className="p-2 bg-slate-900/50 rounded-lg border border-slate-700/50 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-500 font-medium">Era {index + 1}</span>
                    {erasList.length > 1 && (
                      <button
                        onClick={() => handleRemoveEra(index)}
                        className="w-5 h-5 rounded bg-red-500/20 hover:bg-red-500/40 flex items-center justify-center transition-colors"
                      >
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1">Reward</label>
                      <input
                        type="number"
                        step="0.00000001"
                        value={toCoins(era.reward)}
                        onChange={(e) => handleUpdateEra(index, 'reward', toSatoshis(e.target.value))}
                        placeholder="24"
                        className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-white text-xs placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1">Decay %</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={era.decay || 0}
                        onChange={(e) => handleUpdateEra(index, 'decay', parseInt(e.target.value) || 0)}
                        placeholder="0"
                        className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-white text-xs placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1">Halving</label>
                      <input
                        type="number"
                        value={era.halving || 0}
                        onChange={(e) => handleUpdateEra(index, 'halving', parseInt(e.target.value) || 0)}
                        placeholder="blocks"
                        className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-white text-xs placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1">Era End</label>
                      <input
                        type="number"
                        value={era.eraend || 0}
                        onChange={(e) => handleUpdateEra(index, 'eraend', parseInt(e.target.value) || 0)}
                        placeholder="0=none"
                        className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-white text-xs placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3 !h-3 !bg-violet-500 !border-2 !border-slate-800"
      />
    </motion.div>
  );
};

export default EraNode;
